/**
 * @fileoverview AR-924 — Maps a failed HTTP call of the teacher-upload routes to a `TeacherDeliverableError`
 * (422 body shapes: plain string, `{ files: [...] }` or array of messages — see ../ports.ts).
 */

import axios from 'axios'
import { TeacherDeliverableError, type TeacherDeliverableErrorKind } from '../ports'

const UNPROCESSABLE = 422

const STRING_KINDS: ReadonlyArray<[RegExp, TeacherDeliverableErrorKind]> = [
  [/already submitted/i, 'student_submitted'],
  [/project is finished/i, 'project_finished'],
  [/not found/i, 'not_found'],
  [/work completed date/i, 'invalid_date'],
]

function kindOfMessage(message: string): TeacherDeliverableErrorKind {
  const match = STRING_KINDS.find(([pattern]) => pattern.test(message))
  return match ? match[1] : 'unknown'
}

function isFileRejection(body: unknown): body is { files: string[] } {
  return typeof body === 'object' && body !== null && Array.isArray((body as { files?: unknown }).files)
}

export function toTeacherDeliverableError(error: unknown): TeacherDeliverableError {
  if (error instanceof TeacherDeliverableError) return error
  if (axios.isCancel(error)) return new TeacherDeliverableError('cancelled')
  if (!axios.isAxiosError(error) || error.response?.status !== UNPROCESSABLE) {
    return new TeacherDeliverableError('unknown')
  }

  const body: unknown = error.response.data
  if (typeof body === 'string') return new TeacherDeliverableError(kindOfMessage(body), [body])
  if (isFileRejection(body)) return new TeacherDeliverableError('file_rejected', body.files)
  if (Array.isArray(body)) return new TeacherDeliverableError('invalid_url', body.map(String))
  return new TeacherDeliverableError('unknown')
}
